import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Ban,
  CircleCheck,
  CircleX,
  Clock,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { IssuanceState } from './api';

type Tone = {
  icon: LucideIcon;
  /** Whether the leading icon spins (used for the still-moving in-flight states). */
  spin?: boolean;
  className: string;
};

// One colour family per lifecycle bucket: in-flight = sky, issued = emerald, needs-eyes = amber,
// rejected = red, abandoned = muted. Colours are inline (not Badge variants) so light + dark both read.
const SKY = 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300';
const EMERALD = 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
const AMBER = 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300';
const RED = 'border-destructive/40 bg-destructive/10 text-destructive';
const MUTED = 'border-border bg-muted text-muted-foreground';

const TONES: Record<IssuanceState, Tone> = {
  // QUEUED is *waiting* for the worker (not actively working), so it shows a static clock, not a spinner.
  QUEUED: { icon: Clock, className: SKY },
  SUBMITTING: { icon: Loader2, spin: true, className: SKY },
  AWAITING_KSEF: { icon: Loader2, spin: true, className: SKY },
  RETRY_WAIT: { icon: Loader2, spin: true, className: AMBER },
  ACCEPTED: { icon: CircleCheck, className: EMERALD },
  COMPLETED: { icon: CircleCheck, className: EMERALD },
  NEEDS_ATTENTION: { icon: AlertTriangle, className: AMBER },
  REJECTED: { icon: CircleX, className: RED },
  ABANDONED: { icon: Ban, className: MUTED },
};

/**
 * The lifecycle state of an outbound invoice as a toned, iconful badge with a localized label. The
 * in-flight states spin their icon to signal live activity — set `animate={false}` where the badge marks
 * a *past* state (e.g. a timeline entry), so history doesn't look like it's still churning.
 */
export function StatusBadge({
  state,
  className,
  animate = true,
}: {
  state: IssuanceState;
  className?: string;
  animate?: boolean;
}) {
  const { t } = useTranslation();
  const tone = TONES[state];
  const Icon = tone.icon;
  return (
    <Badge variant="outline" className={cn('gap-1', tone.className, className)}>
      <Icon className={cn('size-3', tone.spin && animate && 'animate-spin')} />
      {t(`invoices.outbound.state.${state}`)}
    </Badge>
  );
}
